import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class TokenStorageService {
  private readonly tokenKey = 'access_token';
  private readonly refreshTokenKey = 'refresh_token';

  private static hasExp(value: unknown): value is { exp: number } {
    return (
      typeof value === 'object' &&
      value !== null &&
      'exp' in value &&
      typeof (value as { exp?: unknown }).exp === 'number'
    );
  }

  setToken(token: string): void {
    localStorage.setItem(this.tokenKey, token);
  }

  getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  setRefreshToken(token: string): void {
    localStorage.setItem(this.refreshTokenKey, token);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(this.refreshTokenKey);
  }

  clearToken(): void {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.refreshTokenKey);
  }

  hasToken(): boolean {
    return !!this.getToken();
  }

  /**
   * Decodifica un JWT y retorna su payload
   */
  private decodeToken(token: string): unknown {
    try {
      const payload = token.split('.')[1];
      const decoded = atob(payload);
      return JSON.parse(decoded);
    } catch (e) {
      console.error('Error decoding token:', e);
      return null;
    }
  }

  /**
   * Obtiene la fecha de expiración del token actual
   * @returns Date de expiración o null si no hay token o no se puede decodificar
   */
  getTokenExpiration(): Date | null {
    const token = this.getToken();
    if (!token) return null;

    const decoded = this.decodeToken(token);
    if (!TokenStorageService.hasExp(decoded)) return null;

    // exp viene en segundos, Date espera milisegundos
    return new Date(decoded.exp * 1000);
  }

  /**
   * Verifica si el token actual está expirado
   * @returns true si el token está expirado, false si no
   */
  isTokenExpired(): boolean {
    const expiration = this.getTokenExpiration();
    if (!expiration) return true; // Si no hay token o no se puede decodificar, considerarlo expirado

    return expiration.getTime() < Date.now();
  }

  /**
   * Obtiene el tiempo restante en milisegundos hasta que expire el token
   * @returns milisegundos hasta expiración, 0 si ya expiró o no hay token
   */
  getTimeUntilExpiration(): number {
    const expiration = this.getTokenExpiration();
    if (!expiration) return 0;

    const timeRemaining = expiration.getTime() - Date.now();
    return timeRemaining > 0 ? timeRemaining : 0;
  }
}
